import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ImgixNextImage } from "@components/imgixNextImage";
import { Bookmark, Gear, OpenBook, SignOut } from "@icons";
import { tagular } from "@cohesion/tagular";
import { preventLayoutShift } from "./utils";

export function ProfilePanel({
  hasSession = false,
  user = null,
  alertDismissed = false,
  disableAlert = false,
}) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { nickname = "unknown", picture = null } = user || {};
  const pictureUrl = !picture?.includes("gravatar") ? picture : null;

  // Get the correct top position based on alert status
  const getPanelTopPosition = () => {
    if (disableAlert || alertDismissed) {
      return "top-[68px]";
    }
    return "top-[120px]";
  };

  // Handle body overflow when panel is open/hovered
  useEffect(() => {
    // Use the shared utility function
    preventLayoutShift(isPanelOpen || isHovering);

    // Clean up
    return () => {
      preventLayoutShift(false);
    };
  }, [isPanelOpen, isHovering]);

  // Handle hover events
  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  // Handle profile button click
  const handleProfileClick = () => {
    setIsPanelOpen(!isPanelOpen);

    // Track click
    tagular("click", {
      actionOutcome: isPanelOpen ? "CLOSE" : "INTERNALLINK",
      outboundUrl: "null",
      webElement: {
        location: "HEADER",
        elementType: "LINK",
        position: "GLOBAL NAV BAR",
        text: isPanelOpen ? "PROFILE-X" : "PROFILE-NAV",
      },
    });
  };

  // Handle link clicks for tracking
  const handleLinkClick = event => {
    const text = event.currentTarget.getAttribute("data-title");
    const url = event.currentTarget.getAttribute("data-url");

    tagular("click", {
      actionOutcome: "INTERNALLINK",
      outboundUrl: url,
      webElement: {
        location: "HEADER",
        elementType: "LINK",
        position: "GLOBAL SUB MENU",
        text: text.toUpperCase(),
      },
    });
  };

  // Handle sign in button click
  const handleSignInClick = () => {
    tagular("click", {
      actionOutcome: "INTERNALLINK",
      outboundUrl: "/api/auth/login",
      webElement: {
        location: "HEADER",
        elementType: "LINK",
        position: "GLOBAL NAV BAR",
        text: "SIGN IN",
      },
    });
  };

  // Determine if panel should be shown
  const shouldShowPanel = isPanelOpen || isHovering;

  return (
    <div
      className="relative py-2 group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {hasSession ? (
        <>
          <button
            id="profilePanelTrigger"
            type="button"
            className="relative flex items-center h-full text-sm shrink-0 focus:outline-hidden"
            aria-expanded={shouldShowPanel}
            aria-controls="profilePanel"
            aria-label="User menu"
            onClick={handleProfileClick}
          >
            <span className="absolute -inset-1.5" />
            <span className="sr-only">Open user menu</span>
            {picture && (
              <>
                <ImgixNextImage
                  src={pictureUrl}
                  alt={`picture of ${nickname}`}
                  className="w-10 h-10 rounded-full"
                  type="user"
                  width={40}
                  height={40}
                  imgixParams={{
                    auto: "format",
                    fit: "crop",
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="16"
                  viewBox="0 0 18 16"
                  fill="none"
                  className={`menu-arrow absolute left-0 right-0 -bottom-1 mx-auto translate-y-full text-white ${shouldShowPanel ? "block" : "hidden"}`}
                >
                  <path
                    d="M8.49348 0L17.9794 15.5138H0.0615234L8.49348 0Z"
                    fill="currentColor"
                  />
                </svg>
              </>
            )}
          </button>

          <div
            id="profilePanel"
            role="menu"
            aria-labelledby="profilePanelTrigger"
            className={`fixed left-[min(calc(50vw+720px-250px),calc(100vw-280px-32px))] w-[280px] bg-white shadow-lg transition-all duration-200 ${getPanelTopPosition()} ${
              shouldShowPanel
                ? "visible opacity-100 z-60"
                : "invisible opacity-0 z-50"
            }`}
          >
            <div className="p-8 space-y-5">
              <a
                href="/profile/saves"
                className="flex gap-x-2.5 items-center group-hover:bg-white hover:text-blue text-black transition-colors"
                role="menuitem"
                data-title="saves"
                data-url="/profile/saves"
                onClick={handleLinkClick}
              >
                <Bookmark className="hover:text-blue" /> Saves
              </a>
              <a
                href="/profile/library"
                className="flex gap-x-2.5 items-center group-hover:bg-white hover:text-blue text-black transition-colors"
                role="menuitem"
                data-title="library"
                data-url="/profile/library"
                onClick={handleLinkClick}
              >
                <OpenBook className="hover:text-blue" />
                eLibrary
              </a>
              <a
                href="/profile/settings"
                className="flex gap-x-2.5 items-center group-hover:bg-white hover:text-blue text-black transition-colors"
                role="menuitem"
                data-title="settings"
                data-url="/profile/settings"
                onClick={handleLinkClick}
              >
                <Gear className="hover:text-blue shrink-0" />
                Account settings
              </a>
            </div>
            <a
              href="/api/auth/logout"
              className="flex bg-black-100 gap-x-2.5 items-center p-4 font-semibold text-black hover:bg-blue hover:text-white w-full"
              role="menuitem"
              data-title="LOGOUT"
              data-url="/api/auth/logout"
              onClick={handleLinkClick}
            >
              <SignOut className="hover:text-white" />
              Sign out
            </a>
          </div>
        </>
      ) : (
        <div className="flex items-center h-full">
          <a
            className="relative z-10 px-4 py-2 text-white pointer-events-auto rounded-pill bg-blue btn btn-primary focus:outline-hidden"
            href="/api/auth/login"
            aria-label="Sign in"
            onClick={handleSignInClick}
          >
            Sign In
          </a>
        </div>
      )}
    </div>
  );
}

ProfilePanel.propTypes = {
  hasSession: PropTypes.bool,
  user: PropTypes.shape({
    nickname: PropTypes.string,
    picture: PropTypes.string,
  }),
  alertDismissed: PropTypes.bool,
  disableAlert: PropTypes.bool,
};

export default ProfilePanel;
